import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationType, NotificationPreference } from '../types/Notification';
import notificationApi from '../services/notificationApi';
import {
  BellIcon,
  ChevronLeftIcon,
  MoonIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  PhoneIcon,
  DocumentIcon,
  DocumentTextIcon,
  CogIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/Layout';

const NotificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Quiet hours state
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState('22:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [typesResponse, prefsResponse] = await Promise.all([
        notificationApi.getNotificationTypes(),
        notificationApi.getPreferences(),
      ]);

      setNotificationTypes(typesResponse.types);
      setPreferences(prefsResponse.preferences);

      // Extract quiet hours from first preference (they're the same for all)
      if (prefsResponse.preferences.length > 0) {
        const firstPref = prefsResponse.preferences[0];
        setQuietHoursEnabled(firstPref.quietHoursEnabled || false);
        setQuietHoursStart(firstPref.quietHoursStart || '22:00');
        setQuietHoursEnd(firstPref.quietHoursEnd || '08:00');
      }
    } catch (err) {
      setError('Failed to load notification settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'leads':
        return UserGroupIcon;
      case 'tasks':
        return ClipboardDocumentListIcon;
      case 'calls':
        return PhoneIcon;
      case 'documents':
        return DocumentIcon;
      case 'forms':
        return DocumentTextIcon;
      case 'system':
        return CogIcon;
      case 'automation':
        return BoltIcon;
      default:
        return BellIcon;
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getPreferenceForType = (typeId: number): NotificationPreference | undefined => {
    return preferences.find(p => p.notificationTypeId === typeId);
  };

  const updatePreference = async (typeId: number, field: 'inAppEnabled' | 'emailEnabled', value: boolean) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await notificationApi.updatePreferences([{ notificationTypeId: typeId, [field]: value }]);
      
      // Update local state
      setPreferences(prev => {
        return prev.map(p => 
          p.notificationTypeId === typeId 
            ? { ...p, [field]: value }
            : p
        );
      });

      setSuccess('Preference updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to update preference');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateQuietHours = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await notificationApi.updateQuietHours({
        enabled: quietHoursEnabled,
        start: quietHoursStart,
        end: quietHoursEnd,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Update local preferences state
      setPreferences(prev => prev.map(p => ({
        ...p,
        quietHoursEnabled,
        quietHoursStart,
        quietHoursEnd,
      })));

      setSuccess('Quiet hours updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to update quiet hours');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleAllInCategory = async (category: string, field: 'inAppEnabled' | 'emailEnabled', enable: boolean) => {
    const typesInCategory = notificationTypes.filter(t => t.category === category);
    
    setSaving(true);
    setError(null);

    try {
      const updates = typesInCategory.map(type => ({
        notificationTypeId: type.id,
        [field]: enable,
      }));
      
      await notificationApi.updatePreferences(updates);

      // Update local state
      const typeIds = typesInCategory.map(t => t.id);
      setPreferences(prev => 
        prev.map(p => 
          typeIds.includes(p.notificationTypeId)
            ? { ...p, [field]: enable }
            : p
        )
      );

      setSuccess(`${enable ? 'Enabled' : 'Disabled'} ${field === 'inAppEnabled' ? 'in-app' : 'email'} notifications for ${category}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to update preferences');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Group notification types by category
  const groupedTypes = notificationTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {} as Record<string, NotificationType[]>);

  const categoryOrder = ['leads', 'tasks', 'calls', 'documents', 'forms', 'system', 'automation'];

  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700';
      case 'high':
        return 'bg-orange-100 text-orange-700';
      case 'medium':
        return 'bg-blue-100 text-blue-700';
      case 'low':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <Layout title="Notification Settings">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Notification Settings" subtitle="Manage how and when you receive notifications">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/notifications')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back to Notifications
        </button>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {/* Quiet Hours Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <MoonIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Quiet Hours</h2>
                <p className="text-sm text-gray-500">
                  Pause email notifications during specific hours
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700">Enable quiet hours</span>
              <button
                onClick={() => setQuietHoursEnabled(!quietHoursEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                  quietHoursEnabled ? 'bg-gray-800' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    quietHoursEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {quietHoursEnabled && (
              <div className="flex items-center gap-4 mt-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">From</label>
                  <input
                    type="time"
                    value={quietHoursStart}
                    onChange={(e) => setQuietHoursStart(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">To</label>
                  <input
                    type="time"
                    value={quietHoursEnd}
                    onChange={(e) => setQuietHoursEnd(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>
                <div className="pt-6">
                  <button
                    onClick={updateQuietHours}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification Types by Category */}
        {categoryOrder.map((category) => {
          const types = groupedTypes[category];
          if (!types || types.length === 0) return null;

          const CategoryIcon = getCategoryIcon(category);

          return (
            <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <CategoryIcon className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {getCategoryLabel(category)} Notifications
                      </h2>
                      <p className="text-sm text-gray-500">
                        {types.length} notification type{types.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => toggleAllInCategory(category, 'inAppEnabled', true)}
                      className="text-xs text-gray-600 hover:text-gray-900"
                    >
                      Enable all in-app
                    </button>
                    <button
                      onClick={() => toggleAllInCategory(category, 'emailEnabled', true)}
                      className="text-xs text-gray-600 hover:text-gray-900"
                    >
                      Enable all email
                    </button>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {/* Header Row */}
                <div className="px-6 py-3 bg-gray-50 grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="col-span-6">Notification</div>
                  <div className="col-span-2 text-center">Priority</div>
                  <div className="col-span-2 text-center">In-App</div>
                  <div className="col-span-2 text-center">Email</div>
                </div>

                {types.map((type) => {
                  const pref = getPreferenceForType(type.id);
                  const inAppEnabled = pref?.inAppEnabled ?? true;
                  const emailEnabled = pref?.emailEnabled ?? type.defaultChannels.includes('email');

                  return (
                    <div key={type.id} className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-gray-50">
                      <div className="col-span-6">
                        <h4 className="text-sm font-medium text-gray-900">{type.displayName}</h4>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getPriorityClass(type.defaultPriority)}`}>
                          {type.defaultPriority}
                        </span>
                      </div>
                      <div className="col-span-2 text-center">
                        <button
                          onClick={() => updatePreference(type.id, 'inAppEnabled', !inAppEnabled)}
                          disabled={saving}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                            inAppEnabled ? 'bg-gray-800' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              inAppEnabled ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                      <div className="col-span-2 text-center">
                        <button
                          onClick={() => updatePreference(type.id, 'emailEnabled', !emailEnabled)}
                          disabled={saving}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                            emailEnabled ? 'bg-gray-800' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              emailEnabled ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default NotificationSettingsPage;
